<template>
  <div class="terms-page">
    <Header />
    
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="hero-content">
          <h1>Terms and Conditions</h1>
          <p>Last updated: May 7, 2026</p>
          <div class="breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="separator">/</span>
            <span class="current">Terms & Conditions</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <section class="content-section">
      <div class="container">
        <div class="content-wrapper">
          <!-- Sidebar Navigation -->
          <aside class="sidebar">
            <div class="sidebar-sticky">
              <h3>Contents</h3>
              <ul class="sidebar-nav">
                <li><a href="#agreement" @click.prevent="scrollTo('agreement')">1. Agreement to Terms</a></li>
                <li><a href="#definitions" @click.prevent="scrollTo('definitions')">2. Definitions</a></li>
                <li><a href="#account" @click.prevent="scrollTo('account')">3. Account Registration</a></li>
                <li><a href="#purchases" @click.prevent="scrollTo('purchases')">4. Purchases & Payments</a></li>
                <li><a href="#shipping" @click.prevent="scrollTo('shipping')">5. Shipping & Delivery</a></li>
                <li><a href="#returns" @click.prevent="scrollTo('returns')">6. Returns & Refunds</a></li>
                <li><a href="#seller" @click.prevent="scrollTo('seller')">7. Seller Terms</a></li>
                <li><a href="#token" @click.prevent="scrollTo('token')">8. EKU Token</a></li>
                <li><a href="#privacy" @click.prevent="scrollTo('privacy')">9. Privacy & Data</a></li>
                <li><a href="#prohibited" @click.prevent="scrollTo('prohibited')">10. Prohibited Activities</a></li>
                <li><a href="#liability" @click.prevent="scrollTo('liability')">11. Limitation of Liability</a></li>
                <li><a href="#termination" @click.prevent="scrollTo('termination')">12. Termination</a></li>
                <li><a href="#governing" @click.prevent="scrollTo('governing')">13. Governing Law</a></li>
                <li><a href="#contact" @click.prevent="scrollTo('contact')">14. Contact Us</a></li>
              </ul>
            </div>
          </aside>

          <!-- Main Content Area -->
          <main class="main-content">
            <div class="terms-content">
              <!-- Section 1 -->
              <section id="agreement" class="terms-section">
                <h2>1. Agreement to Terms</h2>
                <p>By accessing or using the EKU MARKET website, mobile application, or any related services (collectively, the "Platform"), you agree to be bound by these Terms and Conditions ("Terms"). If you disagree with any part of these Terms, you may not access or use the Platform.</p>
                <p>EKU MARKET COMPANY LTD ("Company," "we," "us," or "our") reserves the right to modify these Terms at any time. Changes become effective immediately upon posting. Your continued use of the Platform constitutes acceptance of any changes.</p>
              </section>

              <!-- Section 2 -->
              <section id="definitions" class="terms-section">
                <h2>2. Definitions</h2>
                <ul>
                  <li><strong>"Buyer" or "Customer"</strong> – Any user who purchases products through the Platform.</li>
                  <li><strong>"Seller" or "Agent"</strong> – Verified third-party vendor selling products on the Platform.</li>
                  <li><strong>"Platform"</strong> – The EKU MARKET website, mobile app, and all related services.</li>
                  <li><strong>"EKU Token"</strong> – Digital reward token earned through Platform activities.</li>
                  <li><strong>"Order"</strong> – A purchase request submitted by a Buyer through the Platform.</li>
                  <li><strong>"Content"</strong> – All information, data, text, images, and materials on the Platform.</li>
                </ul>
              </section>

              <!-- Section 3 -->
              <section id="account" class="terms-section">
                <h2>3. Account Registration</h2>
                <h3>3.1 Eligibility</h3>
                <p>You must be at least 18 years old to create an account. By registering, you represent that you are of legal age and have the capacity to enter binding contracts.</p>
                
                <h3>3.2 Account Responsibility</h3>
                <p>You are solely responsible for:</p>
                <ul>
                  <li>Maintaining the confidentiality of your password and account</li>
                  <li>All activities that occur under your account</li>
                  <li>Immediately notifying us of any unauthorized account access</li>
                </ul>
                
                <h3>3.3 Accurate Information</h3>
                <p>You agree to provide accurate, current, and complete information during registration and to update it promptly when changes occur. We reserve the right to suspend or terminate accounts with false or outdated information.</p>
              </section>

              <!-- Section 4 -->
              <section id="purchases" class="terms-section">
                <h2>4. Purchases & Payments</h2>
                <h3>4.1 Order Process</h3>
                <p>When you place an order through the Platform, you are making an offer to purchase the selected products. We reserve the right to accept or decline any order for any reason, including product availability, pricing errors, or suspected fraud.</p>
                
                <h3>4.2 Pricing</h3>
                <p>All prices are listed in Tanzanian Shillings (TZS). Prices are subject to change without notice. Taxes, shipping, and handling fees may apply and will be disclosed before order confirmation.</p>
                
                <h3>4.3 Payment Methods</h3>
                <p>We accept the following payment methods:</p>
                <ul>
                  <li>Mobile Money (Halopesa, Airtel Money, Tigo Pesa, M-Pesa)</li>
                  <li>Bank Transfer</li>
                  <li>Credit/Debit Cards (Visa, Mastercard)</li>
                  <li>Cash on Delivery (for approved products)</li>
                </ul>
                
                <h3>4.4 Escrow Protection</h3>
                <p>EKU MARKET holds all customer payments in a secure escrow account. Funds are released to the Seller only after delivery confirmation or 5 working days pass without reported issues, as detailed in our Payment & Escrow Policy.</p>
                
                <h3>4.5 Order Confirmation</h3>
                <p>After placing an order, you will receive a confirmation email or SMS. This confirmation does not guarantee order acceptance. In case of issues (e.g., out of stock), we will notify you and process a full refund.</p>
              </section>

              <!-- Section 5 -->
              <section id="shipping" class="terms-section">
                <h2>5. Shipping & Delivery</h2>
                <h3>5.1 Delivery Timeframes</h3>
                <p>Domestic deliveries (Tanzania) typically take 1–7 business days. International deliveries vary by destination. These are estimates and not guarantees.</p>
                
                <h3>5.2 Shipping Costs</h3>
                <p>Shipping costs are calculated at checkout based on product weight, seller policies, and destination. Some products may qualify for free shipping Tanzania-wide.</p>
                
                <h3>5.3 Risk of Loss</h3>
                <p>Title and risk of loss for products pass to you upon delivery. For Cash on Delivery orders, risk passes when you accept the package.</p>
                
                <h3>5.4 Failed Deliveries</h3>
                <p>After two failed delivery attempts, the order will be returned to EKU MARKET. You may be responsible for re-shipping costs.</p>
              </section>

              <!-- Section 6 -->
              <section id="returns" class="terms-section">
                <h2>6. Returns & Refunds</h2>
                <h3>6.1 Return Window</h3>
                <p>You have 7 days from delivery to request a return for eligible products. Electronics and perishable items have restricted return policies as detailed on our Returns & Refunds page.</p>
                
                <h3>6.2 Return Conditions</h3>
                <p>To be eligible for return, products must be:</p>
                <ul>
                  <li>Unused and in original condition</li>
                  <li>In original packaging with all accessories</li>
                  <li>Accompanied by proof of purchase</li>
                </ul>
                
                <h3>6.3 Refund Process</h3>
                <p>Approved refunds are issued to the original payment method within 7 working days of return approval. EKU MARKET covers return shipping costs for eligible returns.</p>
                
                <h3>6.4 Exchanges</h3>
                <p>Exchanges are offered when stock permits and the customer agrees, instead of a cash refund.</p>
              </section>

              <!-- Section 7 -->
              <section id="seller" class="terms-section">
                <h2>7. Seller Terms</h2>
                <h3>7.1 Verification</h3>
                <p>All Sellers must complete our verification process, including providing business documents, NIDA numbers, and proof of permanent office. We reserve the right to reject any Seller application.</p>
                
                <h3>7.2 Seller Obligations</h3>
                <p>Sellers agree to:</p>
                <ul>
                  <li>Provide accurate product descriptions and images</li>
                  <li>Process orders within 2 business days</li>
                  <li>Provide valid tracking numbers for all shipments</li>
                  <li>Honor all platform return policies</li>
                  <li>Not sell prohibited or counterfeit items</li>
                </ul>
                
                <h3>7.3 Violations</h3>
                <p>Violation of these Terms may result in suspension, contract termination, and forfeiture of platform privileges.</p>
              </section>

              <!-- Section 8 -->
              <section id="token" class="terms-section">
                <h2>8. EKU Token</h2>
                <h3>8.1 Token Nature</h3>
                <p>EKU Tokens are digital reward points with potential future cryptocurrency features. Tokens are not legal tender and have no guaranteed value outside the Platform.</p>
                
                <h3>8.2 Earning Tokens</h3>
                <p>Customers earn tokens through eligible activities including account creation, purchases, reviews, and referrals. Earning rates are subject to change.</p>
                
                <h3>8.3 Using Tokens</h3>
                <p>Tokens may be used for purchases, discounts, and future features including cash withdrawal and external trading (when available). Tokens cannot be exchanged for cash except through official Platform mechanisms.</p>
                
                <h3>8.4 Token Expiration</h3>
                <p>Tokens do not expire with active account use. Extended inactivity (2+ years without login) may result in token dormancy fees or forfeiture.</p>
                
                <h3>8.5 Risk Disclosure</h3>
                <p>Token value may fluctuate. EKU MARKET does not guarantee token value appreciation or exchange rates. Customers assume all risks associated with token holding and use.</p>
              </section>

              <!-- Section 9 -->
              <section id="privacy" class="terms-section">
                <h2>9. Privacy & Data Protection</h2>
                <h3>9.1 Data Collection</h3>
                <p>We collect personal information including name, contact details, payment information, and usage data. This information is used to provide Platform services, process transactions, and improve user experience.</p>
                
                <h3>9.2 Data Sharing</h3>
                <p>We do not sell or share customer information with third parties except as necessary to fulfill orders (e.g., sharing shipping address with delivery partners) or as required by law.</p>
                
                <h3>9.3 Data Security</h3>
                <p>We implement industry-standard security measures to protect your information. However, no online transmission is 100% secure, and we cannot guarantee absolute security.</p>
                
                <h3>9.4 Your Rights</h3>
                <p>You may request access to, correction of, or deletion of your personal data by contacting support@ekumarket.com.</p>
              </section>

              <!-- Section 10 -->
              <section id="prohibited" class="terms-section">
                <h2>10. Prohibited Activities</h2>
                <p>You agree not to:</p>
                <ul>
                  <li>Use the Platform for any illegal purpose</li>
                  <li>Attempt to hack, disrupt, or overload our servers</li>
                  <li>Scrape, copy, or reverse-engineer Platform content</li>
                  <li>Create fake accounts or manipulate reviews/ratings</li>
                  <li>Intentionally provide false information</li>
                  <li>Interfere with other users' access or experience</li>
                  <li>Use automated bots or scripts to interact with the Platform</li>
                </ul>
              </section>

              <!-- Section 11 -->
              <section id="liability" class="terms-section">
                <h2>11. Limitation of Liability</h2>
                <p>To the maximum extent permitted by law:</p>
                <ul>
                  <li>The Platform is provided "as is" without warranties of any kind</li>
                  <li>We are not liable for indirect, incidental, or consequential damages</li>
                  <li>Our total liability is limited to the amount you paid for the specific order in dispute</li>
                  <li>We are not responsible for Seller actions, product quality, or delivery delays beyond our control</li>
                </ul>
              </section>

              <!-- Section 12 -->
              <section id="termination" class="terms-section">
                <h2>12. Termination</h2>
                <p>We may suspend or terminate your account at any time for violation of these Terms, fraudulent activity, or any conduct harmful to the Platform or other users.</p>
                <p>Upon termination:</p>
                <ul>
                  <li>Your right to use the Platform ceases immediately</li>
                  <li>Outstanding orders will be cancelled and refunded (if applicable)</li>
                  <li>Accrued EKU Tokens may be forfeited (at our discretion)</li>
                </ul>
              </section>

              <!-- Section 13 -->
              <section id="governing" class="terms-section">
                <h2>13. Governing Law</h2>
                <p>These Terms are governed by and construed in accordance with the laws of the United Republic of Tanzania. Any disputes arising from these Terms or your use of the Platform shall be resolved exclusively in the courts of Shinyanga, Tanzania.</p>
                <p>For consumer disputes, we encourage you to contact us first at support@ekumarket.com for resolution before pursuing legal action.</p>
              </section>

              <!-- Section 14 -->
              <section id="contact" class="terms-section">
                <h2>14. Contact Us</h2>
                <p>For questions about these Terms, please contact us:</p>
                <ul class="contact-list">
                  <li><strong>Email:</strong> support@ekumarket.com</li>
                  <li><strong>WhatsApp/Phone:</strong> +255 745 450 431</li>
                  <li><strong>Address:</strong> Shinyanga, Tanzania</li>
                  <li><strong>Business Hours:</strong> Monday–Friday, 8:00 AM – 6:00 PM EAT</li>
                </ul>
              </section>

              <!-- Acknowledgment -->
              <div class="acknowledgment">
                <p><strong>By using EKU MARKET, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.</strong></p>
                <p class="last-updated">Last Updated: May 7, 2026 | Version 1.0</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
import Header from "../components/Header.vue";
import Footer from "../components/Footer.vue";
import "@fortawesome/fontawesome-free/css/all.min.css";

const scrollTo = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
};
</script>

<style scoped>
/* ============================================
   BASE STYLES
   ============================================ */
.terms-page {
  overflow-x: hidden;
}

.container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 20px;
}

/* ============================================
   HERO SECTION
   ============================================ */
.hero-section {
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
  padding: 80px 0 60px;
  text-align: center;
}

.hero-content h1 {
  font-size: 3rem;
  color: white;
  margin-bottom: 16px;
  font-weight: 700;
}

.hero-content p {
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
  margin-bottom: 20px;
}

.breadcrumb {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.breadcrumb a {
  color: #D4AF37;
  text-decoration: none;
  transition: color 0.3s ease;
}

.breadcrumb a:hover {
  color: #FFD700;
  text-decoration: underline;
}

.breadcrumb .separator {
  color: rgba(255, 255, 255, 0.5);
}

.breadcrumb .current {
  color: rgba(255, 255, 255, 0.7);
}

/* ============================================
   CONTENT SECTION
   ============================================ */
.content-section {
  padding: 60px 0 80px;
  background: #f8f9fa;
}

.content-wrapper {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 40px;
}

/* ============================================
   SIDEBAR NAVIGATION
   ============================================ */
.sidebar {
  position: relative;
}

.sidebar-sticky {
  position: sticky;
  top: 100px;
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.sidebar h3 {
  font-size: 1.2rem;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #D4AF37;
  display: inline-block;
}

.sidebar-nav {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sidebar-nav li {
  margin-bottom: 12px;
}

.sidebar-nav a {
  color: #555;
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  display: block;
  padding: 4px 0;
}

.sidebar-nav a:hover {
  color: #D4AF37;
  transform: translateX(4px);
}

/* ============================================
   MAIN CONTENT
   ============================================ */
.main-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.terms-content {
  padding: 48px;
}

.terms-section {
  margin-bottom: 48px;
  scroll-margin-top: 80px;
}

.terms-section h2 {
  font-size: 1.8rem;
  color: #1a1a1a;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #D4AF37;
  display: inline-block;
}

.terms-section h3 {
  font-size: 1.3rem;
  color: #333;
  margin: 24px 0 16px;
}

.terms-section p {
  color: #555;
  line-height: 1.7;
  margin-bottom: 16px;
}

.terms-section ul {
  margin: 16px 0;
  padding-left: 24px;
}

.terms-section li {
  color: #555;
  line-height: 1.7;
  margin-bottom: 8px;
}

.terms-section strong {
  color: #1a1a1a;
}

/* Contact List */
.contact-list {
  list-style: none;
  padding: 0;
}

.contact-list li {
  margin-bottom: 12px;
  padding: 8px 0;
}

/* Acknowledgment */
.acknowledgment {
  background: #f8f9fa;
  padding: 24px;
  border-radius: 12px;
  margin-top: 40px;
  text-align: center;
  border-left: 4px solid #D4AF37;
}

.acknowledgment p {
  margin-bottom: 8px;
}

.acknowledgment .last-updated {
  font-size: 0.85rem;
  color: #888;
  margin-top: 8px;
}

/* ============================================
   RESPONSIVE DESIGN
   ============================================ */
/* Tablet */
@media (max-width: 1024px) {
  .hero-content h1 {
    font-size: 2.5rem;
  }
  
  .terms-content {
    padding: 32px;
  }
}

/* Mobile */
@media (max-width: 768px) {
  .hero-section {
    padding: 60px 0 40px;
  }
  
  .hero-content h1 {
    font-size: 2rem;
  }
  
  .content-wrapper {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .sidebar-sticky {
    position: relative;
    top: 0;
  }
  
  .sidebar-nav {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 8px;
  }
  
  .sidebar-nav li {
    margin-bottom: 0;
  }
  
  .terms-content {
    padding: 24px;
  }
  
  .terms-section h2 {
    font-size: 1.5rem;
  }
  
  .terms-section h3 {
    font-size: 1.2rem;
  }
}

/* Small Mobile */
@media (max-width: 480px) {
  .container {
    padding: 0 16px;
  }
  
  .hero-content h1 {
    font-size: 1.6rem;
  }
  
  .terms-content {
    padding: 20px;
  }
  
  .terms-section {
    margin-bottom: 32px;
  }
  
  .sidebar-nav {
    grid-template-columns: 1fr;
  }
  
  .acknowledgment {
    padding: 16px;
  }
}
</style>